import { v4 } from 'uuid';

import { PingPongTracker } from '~/modules/network/rpc/ping-pong-tracker';
import { NetworkMessages } from '~/modules/remote-mic/network/messages';
import {
  SenderInterface,
  ServerTransport,
  transportCloseReason,
} from '~/modules/remote-mic/network/server/transport/interface';
import { getPingTime, pack, unpack } from '~/modules/remote-mic/network/utils';
import isDev from '~/modules/utils/is-dev';
import Listener from '~/modules/utils/listener';

export interface ForwardedMessage {
  t: 'forward';
  sender: string;
  payload: NetworkMessages;
}

interface WebsocketConnectedMessage {
  t: 'connected';
}

interface WebsocketPongMessage {
  t: 'pong';
}

/** Only the mic relay sends these: the game's socket has gone, or come back, without the phones being dropped. */
interface RoomPresenceMessage {
  t: 'room-away' | 'room-back';
}

export type WebsocketMessage =
  | ForwardedMessage
  | WebsocketConnectedMessage
  | WebsocketPongMessage
  | RoomPresenceMessage;

const PING_INTERVAL_MS = 5_000;

/**
 * How long an unanswered ping stands before the socket counts as dead. A browser will happily hold
 * a socket that nothing is coming through any more - `readyState` stays OPEN on a half-open
 * connection, and a socket closed from this end can sit in CLOSING indefinitely if the relay never
 * completes the handshake. Either way `onclose` never fires, so without this the host would never
 * learn it had dropped out of its own room, and every phone would be refused with
 * `peer-unavailable` until the game was reloaded by hand.
 */
const PONG_DEADLINE_MS = 8_000;

/**
 * How long a socket is given to open before the attempt is written off. A network that swallows
 * packets rather than refusing them leaves a socket in CONNECTING for as long as the browser's own
 * timeout, which is minutes - and the room is unreachable for every second of it.
 */
const CONNECT_DEADLINE_MS = 10_000;

/**
 * Messages a second from the relay that mean something is looping rather than working. A room of
 * four phones idles at a handful; singing, with every mic streaming pitch, runs under a hundred.
 * Dev only, and a warning rather than a limit - it exists because the last loop of this kind took
 * twenty seconds to become visible, and by then it was visible as phones falling off the relay.
 */
const TRAFFIC_WARNING_PER_SECOND = 150;

type PeerListener = { peer: string; callback: (data: NetworkMessages) => void };

/**
 * The game's side of the relay: one socket, registered as the room every phone forwards through.
 * Shared by the WebSockets, PartyKit and mic-relay transports, which differ only in the URL they dial.
 */
export abstract class RelayServerTransport
  extends Listener<[NetworkMessages, SenderInterface]>
  implements ServerTransport
{
  public abstract readonly name: ServerTransport['name'];

  protected abstract getUrl(roomId: string): string;

  /**
   * One game page, however many sockets it gets through. The mic relay uses it to tell a game that
   * dropped its socket - and whose phones are still sitting in the room - from a game that was
   * reloaded and has never heard of them.
   */
  private readonly session = v4();

  private connection: WebSocket | null = null;

  /** Hangs up on the socket this transport is currently holding, if any. Rebuilt per connection. */
  private failCurrentConnection: (() => void) | null = null;

  /** Disowns the current socket: whatever it does next is reported to nobody. Rebuilt per connection. */
  private disownCurrentConnection: (() => void) | null = null;

  /**
   * Held here rather than on the socket. A mic outlives the socket it was first heard on - with the
   * mic relay it outlives it deliberately, since a phone is no longer thrown out of the room when
   * the game's socket drops - so anything bound to one particular socket goes deaf on reconnect.
   */
  private peerListeners: PeerListener[] = [];

  private pingPong = new PingPongTracker({
    intervalMs: PING_INTERVAL_MS,
    maxWaitMs: PONG_DEADLINE_MS,
    onMissedPong: () => this.failCurrentConnection?.(),
  });

  public connect(
    roomId: string,
    onConnect: () => void,
    onClose: (reason: transportCloseReason, originalEvent: CloseEvent | null) => void,
  ) {
    // Anything still held from a previous attempt stops being this transport's business here, so a
    // socket that closes late cannot drive the reconnect of the one that replaced it.
    this.dropConnection();

    const connection = new WebSocket(this.getUrl(roomId));
    connection.binaryType = 'arraybuffer';
    this.connection = connection;

    let closeReported = false;
    const reportClosed = (reason: transportCloseReason, event: CloseEvent | null) => {
      if (closeReported) return;
      closeReported = true;
      clearTimeout(openDeadline);
      this.pingPong.stop();
      if (this.connection === connection) {
        this.connection = null;
        this.failCurrentConnection = null;
        this.disownCurrentConnection = null;
      }
      onClose(reason, event);
    };

    const giveUpOn = (reason: transportCloseReason) => {
      connection.close();
      // Reported rather than waited for: `close()` only promises the socket stops being used, not
      // that `onclose` ever arrives.
      reportClosed(reason, null);
    };

    const openDeadline = setTimeout(() => giveUpOn('unreachable'), CONNECT_DEADLINE_MS);
    this.failCurrentConnection = () => giveUpOn('unresponsive');
    this.disownCurrentConnection = () => {
      closeReported = true;
      clearTimeout(openDeadline);
    };

    connection.onopen = () => {
      clearTimeout(openDeadline);
      this.sendEvent({ t: 'register-room', id: roomId, session: this.session } as NetworkMessages);
      onConnect();
      this.pingPong.start(this.sendPing);
    };

    connection.onmessage = (message) => {
      this.countTraffic();
      const payload: WebsocketMessage = unpack(message.data);

      if (payload.t === 'forward') {
        if (!['ping', 'pong', 'freq'].includes(payload?.payload?.t)) console.log('received', payload);

        this.onUpdate(payload.payload, new SenderWrapper(payload.sender, this));
        this.peerListeners.forEach((listener) => {
          if (listener.peer === payload.sender) listener.callback(payload.payload);
        });
      } else if (payload.t === 'pong') {
        this.pingPong.handlePong();
      } else if (payload.t === 'connected' || payload.t === 'room-away' || payload.t === 'room-back') {
        // Acknowledgements and room presence - nothing for the game to do with either. The phones
        // are the ones that act on room-away and room-back.
      } else {
        console.warn('Unknown message type', payload);
      }
    };

    connection.onclose = (event) => reportClosed(event.reason, event);
    connection.onerror = () => reportClosed('error', null);
  }

  public disconnect = () => {
    this.dropConnection();
  };

  /** Lets go of the current socket without reporting a close - the caller already knows. */
  private dropConnection = () => {
    this.pingPong.stop();
    // Disowned first: an attempt still waiting to open has a deadline running, and it must not fire
    // a close against whichever connection has replaced it by then.
    this.disownCurrentConnection?.();
    this.disownCurrentConnection = null;
    this.failCurrentConnection = null;

    const connection = this.connection;
    this.connection = null;
    if (!connection) return;

    connection.onopen = null;
    connection.onmessage = null;
    connection.onclose = null;
    connection.onerror = null;
    connection.close();
  };

  private trafficWindowStartedAt = 0;
  private trafficInWindow = 0;

  private countTraffic = () => {
    if (!isDev()) return;

    const now = getPingTime();
    if (now - this.trafficWindowStartedAt >= 1_000) {
      if (this.trafficInWindow > TRAFFIC_WARNING_PER_SECOND) {
        console.warn(`[remote mic] ${this.trafficInWindow} messages from the relay in a second - something is looping`);
      }
      this.trafficWindowStartedAt = now;
      this.trafficInWindow = 0;
    }
    this.trafficInWindow++;
  };

  private sendPing = () => {
    this.sendEvent({ t: 'ping' });
  };

  private sendEvent(event: NetworkMessages) {
    if (this.connection?.readyState !== WebSocket.OPEN) {
      return;
    }
    this.connection.send(pack(event));
  }

  public getCurrentPing = () => {
    const pingStartedAt = this.pingPong.getPingStartedAt();
    const latency = this.pingPong.getLatency();

    return pingStartedAt ? Math.max(latency, getPingTime() - pingStartedAt) : latency;
  };

  /** Used by the sender handed to each mic, so a mic always writes to the socket the game holds now. */
  public sendToPeer = (peer: string, payload: NetworkMessages) => {
    if (this.connection?.readyState !== WebSocket.OPEN) return;

    if (!['ping', 'pong', 'freq'].includes(payload?.t)) console.log('sending', peer, payload);
    this.connection.send(pack({ t: 'forward', recipients: [peer], payload }));
  };

  public addPeerListener = (peer: string, callback: (data: NetworkMessages) => void) => {
    this.peerListeners = [...this.peerListeners, { peer, callback }];
  };

  public removePeerListener = (peer: string, callback: (data: NetworkMessages) => void) => {
    this.peerListeners = this.peerListeners.filter(
      (listener) => !(listener.peer === peer && listener.callback === callback),
    );
  };

  public removePlayer(playerId: string) {
    // This sends a transport-level control message to the relay to disconnect the peer
    this.sendEvent({ t: 'remove-player', id: playerId });
  }

  /** Closes whatever socket the game is holding. Only the unload path asks for this. */
  public closeCurrentConnection = () => {
    this.connection?.close();
  };
}

/**
 * What a mic writes to. Holds no socket of its own: a mic can outlive several, and one that kept
 * the socket it was registered on would go quietly one-way the moment the game reconnected.
 */
class SenderWrapper implements SenderInterface {
  constructor(
    public peer: string,
    private transport: RelayServerTransport,
  ) {}

  public send = (payload: NetworkMessages) => {
    this.transport.sendToPeer(this.peer, payload);
  };

  public on = (event: string, callback: (data: NetworkMessages) => void) => {
    if (event === 'data') this.transport.addPeerListener(this.peer, callback);
  };

  public off = (event: string, callback: (data: NetworkMessages) => void) => {
    if (event === 'data') this.transport.removePeerListener(this.peer, callback);
  };

  public close = () => {
    this.transport.closeCurrentConnection();
  };
}
